import React from 'react';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../hooks/useTheme';
import { useNavigate } from 'react-router-dom';
import { User, LogOut, Moon, Sun } from 'lucide-react';
import AyudaContextual from './AyudaContextual';

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
  /** Clave de ayuda contextual (config/ayudaContextual.ts). Muestra el botón "?" junto al título. */
  helpSeccion?: string;
}

const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  actions,
  helpSeccion,
}) => {
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getRoleDisplayName = (rol: string) => {
    const roleNames = {
      administrador: 'Administrador',
      chef: 'Chef',
      personal_cocina: 'Personal de Cocina',
      cliente: 'Cliente',
    };
    return roleNames[rol as keyof typeof roleNames] || rol;
  };

  const getRoleColor = (rol: string) => {
    const roleColors = {
      administrador:
        'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      chef: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      personal_cocina:
        'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      cliente:
        'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
    };
    return (
      roleColors[rol as keyof typeof roleColors] ||
      'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'
    );
  };

  return (
    <header className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm shadow-sm border-b border-gray-200/50 dark:border-gray-700/50 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Título de la página */}
          <div className="flex items-center space-x-3">
            <div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                {title}
              </h1>
              {subtitle && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {subtitle}
                </p>
              )}
            </div>
            {helpSeccion && <AyudaContextual seccion={helpSeccion} />}
          </div>

          {/* Acciones personalizadas de la página */}
          <div className="flex items-center space-x-4">
            {actions && (
              <div className="flex items-center space-x-3">{actions}</div>
            )}

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-all duration-200"
              title={isDark ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
            >
              {isDark ? (
                <Sun className="h-5 w-5 text-yellow-500" />
              ) : (
                <Moon className="h-5 w-5 text-gray-600" />
              )}
            </button>

            {/* User info */}
            <div className="flex items-center space-x-3 bg-gray-50 dark:bg-gray-700 rounded-lg px-3 py-2">
              <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-1.5 rounded-full">
                <User className="h-4 w-4 text-white" />
              </div>
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {user?.nombre}
                </p>
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getRoleColor(user?.rol || '')}`}
                >
                  {getRoleDisplayName(user?.rol || '')}
                </span>
              </div>
            </div>

            {/* Logout button */}
            <button
              onClick={handleLogout}
              className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 transition-all duration-200"
              title="Cerrar sesión"
            >
              <LogOut className="h-5 w-5 text-red-600 dark:text-red-400" />
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default PageHeader;
